/**
 * Generic Reference Field Component
 *
 * Input with select icon (and clear icon when value). Opens the entity browser
 * on icon click. For mixed entity types, browser shows entity type dropdown and search.
 * When read-only and a value is set, the display is navigable (like multi-ref badges).
 * In edit mode, ids can be typed or pasted and validated on blur, Enter, or after 1s idle.
 */

import React, { useCallback, useRef, useState } from 'react';
import { TextInput, ActionIcon, Group, Tooltip, Box, Text, Loader, Flex } from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { EntityBrowserDialog } from '../dialogs';
import { FieldLabelWithRequired } from '../entities/renderDynamicField';
import { MultiReferenceEntityBadge } from './MultiReferenceEntityBadge';
import { createEntityRef, type EntityRef } from '../../types';
import { extractReferenceEntityId, resolveEntityReferenceNavigation } from '../../utils/referenceUtils';
import { useReferenceIdInput } from '../../hooks/useReferenceIdInput';

export interface GenericReferenceFieldProps {
    name: string;
    label: string;
    value: string | EntityRef | null | undefined;
    isRequired?: boolean;
    loading?: boolean;
    entityTypes: string[];
    displayName: string;
    onFieldChange: (name: string, value: string | EntityRef) => void;
    onReferenceEntityUpdate: (fieldName: string, entity: EntityRef) => void;
    onReferenceEntityClear: (fieldName: string) => void;
    /** @deprecated No longer used; entity shape is passed from EntityBrowserDialog onConfirm. */
    entityService?: any;
}

const isMixedType = (entityTypes: string[]) => entityTypes.length > 1;

export const GenericReferenceField: React.FC<GenericReferenceFieldProps> = ({
    name,
    label,
    value,
    isRequired = false,
    loading = false,
    entityTypes,
    displayName,
    onFieldChange,
    onReferenceEntityUpdate,
    onReferenceEntityClear
}) => {
    const [browserOpen, setBrowserOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const committedEntityId = extractReferenceEntityId(value);

    const handleReferenceConfirm = (selectedEntities: Array<{ Id: string; DisplayName: string; EntityType: string }>) => {
        if (selectedEntities.length > 0) {
            const entity = selectedEntities[0];
            const ref = createEntityRef({ Id: entity.Id, DisplayName: entity.DisplayName, entityType: entity.EntityType });
            onFieldChange(name, ref);
            onReferenceEntityUpdate(name, ref);
        }
        setBrowserOpen(false);
    };

    const handleClear = () => {
        onFieldChange(name, '');
        onReferenceEntityClear(name);
    };

    const handleIdCommit = useCallback(
        (entity: { Id: string; DisplayName: string; EntityType: string }) => {
            const ref = createEntityRef({
                Id: entity.Id,
                DisplayName: entity.DisplayName,
                entityType: entity.EntityType,
            });
            onFieldChange(name, ref);
            onReferenceEntityUpdate(name, ref);
        },
        [name, onFieldChange, onReferenceEntityUpdate]
    );

    const idInput = useReferenceIdInput({
        enabled: !loading,
        disabled: loading,
        entityId: committedEntityId,
        displayName,
        entityTypes,
        onCommit: handleIdCommit,
        onClear: handleClear,
    });

    const entityIdForTooltip = extractReferenceEntityId(value);
    const navigation = resolveEntityReferenceNavigation(value, entityTypes);
    const showReadOnlyNav = loading && !!navigation;
    const showCommittedNavDisplay =
        !loading &&
        !!navigation &&
        !!committedEntityId &&
        !idInput.isFocused &&
        !idInput.validating &&
        !idInput.validationError;

    const fieldLabel = <FieldLabelWithRequired label={label} required={isRequired} readOnly={loading} />;
    const typeDescription = entityTypes.length > 0 ? `Select from: ${entityTypes.join(', ')}` : undefined;

    const beginIdEdit = () => {
        if (loading) return;
        idInput.handleFocus();
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    const editIcons = (
        <Group gap={4} wrap="nowrap" pr="xs">
            {idInput.validating && <Loader size={16} />}
            <Tooltip label={label ? `Select ${label}` : 'Select'}>
                <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => setBrowserOpen(true)}
                    disabled={loading}
                    aria-label={label ? `Select ${label}` : 'Select'}
                >
                    <IconSearch size={16} />
                </ActionIcon>
            </Tooltip>
            {value && (
                <Tooltip label="Clear">
                    <ActionIcon
                        variant="subtle"
                        size="sm"
                        color="red"
                        onClick={handleClear}
                        disabled={loading}
                        aria-label="Clear"
                    >
                        <IconX size={16} />
                    </ActionIcon>
                </Tooltip>
            )}
        </Group>
    );

    return (
        <>
            {showReadOnlyNav ? (
                <Box mb="sm">
                    <Text component="label" size="sm" fw={isRequired ? 500 : 400} mb={4} display="block">
                        {fieldLabel}
                    </Text>
                    <Flex
                        align="center"
                        mih={36}
                        px="sm"
                        py={6}
                        style={{
                            border: '1px solid var(--mantine-color-default-border)',
                            borderRadius: 'var(--mantine-radius-default)',
                            backgroundColor: 'var(--mantine-color-body)',
                        }}
                    >
                        <MultiReferenceEntityBadge
                            entityId={navigation.entityId}
                            displayName={displayName || navigation.entityId}
                            entityType={navigation.entityType}
                            fallbackEntityTypes={entityTypes}
                            leftClickNavigation="sameTab"
                        />
                    </Flex>
                    {typeDescription && (
                        <Text size="xs" c="dimmed" mt={4}>
                            {typeDescription}
                        </Text>
                    )}
                </Box>
            ) : showCommittedNavDisplay ? (
                <Box mb="sm">
                    <Text component="label" size="sm" fw={isRequired ? 500 : 400} mb={4} display="block">
                        {fieldLabel}
                    </Text>
                    <Flex
                        align="center"
                        justify="space-between"
                        gap="sm"
                        mih={36}
                        px="sm"
                        py={6}
                        style={{
                            border: '1px solid var(--mantine-color-default-border)',
                            borderRadius: 'var(--mantine-radius-default)',
                            backgroundColor: 'var(--mantine-color-body)',
                        }}
                        onDoubleClick={beginIdEdit}
                        title="Double-click to edit ID"
                    >
                        <MultiReferenceEntityBadge
                            entityId={navigation.entityId}
                            displayName={displayName || navigation.entityId}
                            entityType={navigation.entityType}
                            fallbackEntityTypes={entityTypes}
                            leftClickNavigation="sameTab"
                        />
                        {editIcons}
                    </Flex>
                    {typeDescription && (
                        <Text size="xs" c="dimmed" mt={4}>
                            {typeDescription}
                        </Text>
                    )}
                </Box>
            ) : (
                <Tooltip
                    label={entityIdForTooltip ? `ID: ${entityIdForTooltip}` : undefined}
                    disabled={!entityIdForTooltip || loading}
                    withArrow
                    openDelay={300}
                >
                    <TextInput
                        ref={inputRef}
                        label={fieldLabel}
                        value={idInput.draft}
                        placeholder="Enter ID or use search"
                        disabled={loading}
                        error={!!idInput.validationError}
                        description={idInput.validationError ?? typeDescription}
                        onChange={(e) => idInput.handleChange(e.currentTarget.value)}
                        onBlur={idInput.handleBlur}
                        onFocus={idInput.handleFocus}
                        onKeyDown={idInput.handleKeyDown}
                        mb="sm"
                        rightSection={editIcons}
                        rightSectionWidth={value ? (idInput.validating ? 100 : 72) : (idInput.validating ? 68 : 40)}
                    />
                </Tooltip>
            )}

            <EntityBrowserDialog
                open={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onConfirm={handleReferenceConfirm}
                entityType={entityTypes[0] || 'Person'}
                entityTypes={isMixedType(entityTypes) ? entityTypes : undefined}
                title={`Select ${label}`}
                callerName="GenericReferenceField"
                singleSelection={true}
            />
        </>
    );
};
